import React from "react";
import "./Landing.css";
import "./GradePage.css";
import { Email } from "react-obfuscate-email";
import { findBySlug, homeLinkSections, topicHubOf } from "../Shared/Seo/routeSeo.ts";
import { BlueprintCorners } from "../Shared/Theme/Blueprint.tsx";
import { standardText } from "../Shared/Seo/standards.ts";
import { thumbnailFor } from "../Shared/Seo/thumbnails.ts";
import { Seo } from "../Shared/Seo/Seo.tsx";
import { MathFactsGenerator } from "../MathFactsGenerator/MathFactsGenerator.tsx";
import { settingsForRoute, settingsForSharedLink } from "../MathFactsGenerator/RouteSettings.ts";
import { isWorksheetLink } from "../MathFactsGenerator/UrlProcessor/ProcessUrl.tsx";
import { DefaultSettings } from "../MathFactsGenerator/Models.ts";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import { format } from "../Shared/i18n/format.ts";
import { ManipulativePanel } from "../Manipulatives/ManipulativePanel.tsx";

/**
 * SEO landing-page template. Looks up its content by canonical slug in the
 * shared manifest (src/Shared/Seo/routeSeo.json) and hands the generator the
 * page's single <h1>, which it renders in the workbench header (ADR 0061 §4),
 * and the manifest description as the lede under it. The description is the
 * page's own one-line summary, in the page's own language, where the generic
 * lede was the same on every generator page (ADR 0061, 2026-09-29 amendment).
 * The intro copy and the FAQ follow the tool, in two columns. The same
 * manifest entry is baked into the static HTML at build time by
 * scripts/prerender.js, in the same order.
 *
 * The copy used to sit above the tool. It is still in the DOM and still in the
 * prerendered page, so a crawler reads what it read before; a visitor now meets
 * the worksheet first, which was the redesign's whole point.
 *
 * The home route runs through here too. It has no `operation` of its own -
 * it opens with every operator available, or with whatever the query string
 * asks for - so it passes its seed settings in as `initialSettings`. Routing it
 * through this template is what keeps the page a crawler sees identical to the
 * one React renders: the same title, the same copy, from the same manifest
 * entry, rather than a title the generator synthesised at runtime.
 */
export function LandingPage({ slug, initialSettings }: { slug: string; initialSettings?: any }) {
    const entry = findBySlug(slug);
    const t = useTranslation();

    if (!entry) {
        return <MathFactsGenerator initialSettings={initialSettings ?? DefaultSettings} />;
    }

    const thumbnail = thumbnailFor(entry.slug);
    const topicHub = topicHubOf(entry);
    const homeLinks = entry.slug === '/' ? homeLinkSections() : { grades: [], topics: [] };

    // A route with an operation builds its own settings from the manifest,
    // including any per-route overrides (digit ranges, regrouping, page size)
    // that make a grade or skill page a different worksheet rather than the
    // same one under a new heading. The home route has none, so it uses the
    // settings it was handed - the query string, or the defaults.
    //
    // "Copy link" points at the page the sheet was made on, so a landing page
    // can be opened by a worksheet link too. Then it opens that sheet, under
    // its own title and copy, rather than its defaults. Any other query - a
    // bare seed, a campaign tag - leaves the route's settings alone.
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const seedSettings = entry.operation
        ? (isWorksheetLink(search) ? settingsForSharedLink(entry, search) : settingsForRoute(entry))
        : initialSettings ?? DefaultSettings;

    return (
        <>
            <Seo entry={entry} />
            <MathFactsGenerator
                initialSettings={seedSettings}
                embedded={true}
                title={entry.h1}
                lede={entry.description}
                sharePath={entry.slug}
            />

            <div className="landingCopy">
                <section className="landingContent">
                    <h2>{format(t('workbench.copyHeading'), { title: entry.h1 ?? entry.title })}</h2>
                    {entry.intro?.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                    ))}
                    {/* What the sheet practises, and the standards it
                        practises them for (ADR 0065). prerender.js writes the
                        same markup; change both. */}
                    {entry.objectives && entry.objectives.length > 0 && (
                        <div className="landingCovers">
                            <h3>{t('workbench.coversHeading')}</h3>
                            <ul>
                                {entry.objectives.map((objective, i) => (
                                    <li key={i}>{objective}</li>
                                ))}
                            </ul>
                            {entry.standards && entry.standards.length > 0 && (
                                <>
                                    <h3>{t('workbench.standardsHeading')}</h3>
                                    <ul className="landingStandards">
                                        {entry.standards.map((code) => (
                                            <li key={code}>
                                                <span className="standardCode">{code}</span>{' '}
                                                {standardText(code)}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    )}
                    {/* A picture of the sheet (ADR 0069). English pages only,
                        like the objectives above; prerender.js writes the
                        same markup. */}
                    {thumbnail && !entry.locale && (
                        <figure className="landingSample">
                            <img
                                src={thumbnail.src}
                                width={Math.round(thumbnail.width / 2)}
                                height={Math.round(thumbnail.height / 2)}
                                alt={format(t('workbench.sampleAlt'), { title: entry.h1 ?? entry.title })}
                                loading="lazy"
                                decoding="async"
                            />
                            <figcaption>{t('workbench.sampleCaption')}</figcaption>
                        </figure>
                    )}
                    <p className="landingBugReport">
                        <Email email="mathpageshelp@gmail.com">{t('settings.reportBugEmail')}</Email>
                    </p>
                </section>

                {/* A manipulative for the page (ADR 0117); prerender.js writes the same section. */}
                {entry.manipulative && <ManipulativePanel spec={entry.manipulative} />}

                {entry.faq && entry.faq.length > 0 && (
                    <section className="landingFaq">
                        <h2>{t('settings.frequentlyAskedQuestions')}</h2>
                        {entry.faq.map((item, i) => (
                            <div key={i} className="faqItem">
                                <h3>{item.q}</h3>
                                <p>{item.a}</p>
                            </div>
                        ))}
                    </section>
                )}
            </div>

            {/* A topic's hub lists every other page in its topic (ADR 0072),
                as grade hubs do (ADR 0062). After the copy, so the layout
                above it is unchanged. prerender.js writes the same markup. */}
            {topicHub && topicHub.pages.length > 0 && (
                <section className="topicHub">
                    <h2>{topicHub.heading}</h2>
                    <ul className="gradeHubTiles">
                        {topicHub.pages.map((page) => (
                            <li key={page.slug}>
                                <a className="gradeHubTile blueprint" href={page.slug}>
                                    <BlueprintCorners/>
                                    <span className="gradeHubTileTitle">{page.h1}</span>
                                    {page.objectives?.[0] && (
                                        <span className="gradeHubTileBlurb">{page.objectives[0]}</span>
                                    )}
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* The home page links every grade hub and every topic hub
                (ADR 0112), below the copy so the first screen is unchanged.
                prerender.js writes the same markup; change both. */}
            {entry.slug === '/' && (
                <nav className="homeBrowse" aria-label={t('workbench.browseAria')}>
                    <section className="homeBrowseSection">
                        <h2>{t('workbench.browseByGrade')}</h2>
                        <ul className="homeBrowseList">
                            {homeLinks.grades.map((grade) => (
                                <li key={grade.slug}>
                                    <a href={grade.slug}>{grade.h1}</a>
                                </li>
                            ))}
                        </ul>
                    </section>
                    <section className="homeBrowseSection">
                        <h2>{t('workbench.browseByTopic')}</h2>
                        <ul className="homeBrowseList">
                            {homeLinks.topics.map((topic) => (
                                <li key={topic.slug}>
                                    <a href={topic.slug}>{topic.heading}</a>
                                </li>
                            ))}
                        </ul>
                    </section>
                </nav>
            )}
        </>
    );
}

export default LandingPage;
